// English (British)

export default {
  component: {

    supportLink: 'support@recright.com',

    layout: {
      endTime: date => (date ? `open until ${date}` : ''),
    },
    interview: {
      title: 'Answer to the video interview',
      submit: 'Send answers and complete interview',
      continueLater: 'Continue later',
      startInterview: 'Start interview',
      duplicateError: 'You have already participated to this interview. You have received an email from invitations@recright.com which contains a link that can be used to continue the interview.',
      confirmSubmitHeader: 'You have not answered all the interview questions',
      confirmSubmitContent: 'Do you really want to finish the interview?',
      confirmSubmitContentWarning: 'After this, you can\'t complete your answers.',
      submitInterviewHeader: 'Submit your interview answers',
      submitInterviewContent: 'Are you sure you want to submit your interview answers and complete the interview?',
      confirmSubmitButton: 'Send answers and complete interview',
      preview: 'You can preview the video questions here.',
      requiredQuestionsNotAnswered: 'Please answer all required questions before submitting.',

      withdrawLink: 'Not interested in this position anymore? Withdraw from the process here.',
    },

    personalInterview: {
      title: 'Choose a suitable time for the meeting',
      description: [
        'You have been invited to a meeting.',
        'Please select a time that suits you best.',
      ],
      position: 'Position:',
      location: 'Location:',
      time: 'Time:',
      timezoneNotice: [
        'Please keep in mind that the times are shown in your timezone.',
        'Your timezone appears to be:',
        'The appointment timezone is:',
      ],
      success: {
        title: 'All set!',
        description: 'Great! You have scheduled a meeting!! Here\'s your meeting information:',
        linkDescription: [
          'You can add the event to your calendar below.',
          'Or you can copy the link to your calendar app:',
        ],
        link: 'Add to your calendar',
      },
    },

    interviewForm: {
      name: 'Name',
      city: 'Hometown',
      email: 'Email',
      phone: 'Phone Number',
      link: 'Link',
      legendBasicInfo: 'Basic information',
      placeholderName: 'Enter your name',
      placeholderEmail: 'Enter your email',
      placeholderPhone: 'Enter your phone',
      placeholderCity: 'Enter your current hometown',
      placeholderLink: 'Add link, for example your LinkedIn profile.',
      legendAttachments: 'Attachments',
      legendAdditionalFields: 'Additional information',
      required: 'Items marked with an * asterisk are mandatory.',
    },

    sequencedInterview: {
      networkTest: {
        audio: 'Audio',
        video: 'Video',
        yes: 'Yes',
        no: 'No',
        supported: 'Supported',
        quality: 'Quality',
        header: 'Network connection and device test',
        description: 'Now we\'ll run a test to make sure your network connection and hardware needed for the interview (camera and microphone) are working properly. When prompted, please allow the test to use both your camera and microphone and make sure your camera is not covered. The test will take about 30 seconds. We recommend that you speak during the hardware testing.',
        inProgress: 'Testing in progress...',
        networkTestResults: 'Network test results',
        qualityTestResults: 'Quality test results',
        testPassedNetwork: 'Network test passed',
        testFailedNetwork: 'Network test failed',
        testPassedQuality: 'Quality test passed',
        testFailedQuality: 'Quality test failed',
        startTest: 'Start the test',
        startInterview: 'Start the interview',
        testAgain: 'Test again',
        confirmTitle: 'You are about to start your interview',
        confirmWarning: 'When your video interview starts you are automatically moved to view your first interview question. After viewing the question you will be moved to the preparation.',
        confirmText: 'You have a short time to prepare for your answer before each question. After the preparation you have a preset time to answer the question. Please note that you can submit your answer as soon as you are ready even before the preset answering time is up.',
        confirmText2: 'After submitting your answer you’re moved to the next question until there are no more questions.',
        confirmText3: 'Please note that after recording your response to a question, you will have a 30-second window to retake your answer if retakes are permitted for the question. If you do not initiate a retake within this 30-second period, your current answer will be automatically submitted, and you will be directed to the next question.',
        goodLuck: 'Just take a deep breath and you’ll be fine. Good luck!',
        qualityRating: {
          excellent: 'Excellent',
          good: 'Good',
          fair: 'Fair',
          poor: 'Poor',
          bad: 'Bad',
        },
      },
      questionAnswerView: {
        title: title => `Answer the interview ${title}`,
        question: 'Question',
        submitAndNext: 'Submit & Next',
        submitAndFinish: 'Submit & Finish the interview',
        submitSuccess: 'Video submitted successfully!',
        skipText: 'Start recording',
        stop: 'Stop',
        retake: 'Retake',
        take: 'Take',
        takesLeft: 'Takes left',
        skipTextQuestion: 'Ready? Move forward',
        retakeConfirmTitle: 'Are you sure you want to retake your answer for this question?',
        retakeConfirmSubmit: 'Yes, start the retake',
        submitInfoForward: 'Submitting your current answer automatically and moving forward in',
        submitInfoForward2: 'seconds',
        nonAnswerableText: 'This message requires no answer. After this message, you will be moved directly to the next question.',
        nonAnswerableVideo: 'This video requires no answer. After this video, you will be moved directly to the next question.',
        answerTimeInfo: (answerMinutes, answerSeconds) =>
          `You have ${answerMinutes} minutes ${answerSeconds} seconds time to answer the question.`,
      },
      publisher: {
        toNextQuestion: timer => `Moving to the next question in ${timer}`,
        recordingStarts: timer => `The recording starts in ${timer}`,
      },
      errors: {
        tooManyConnections: 'This connection was closed because you already have a connection open in another tab or window.',
        unsupportedBrowser: 'Unfortunately this operation is not supported on your browser. Please use a different browser.',
        networkError: 'No Internet connection.',
        reconnecting: 'The connection was interrupted. Trying to reconnect.',
        generalError: 'Something went wrong. Please refresh the page and try again.',
        allowMediaDevicesError: 'This operation failed to obtain media devices (camera or microphone). Please make sure you have allowed the browser to use your camera and microphone.',
        noAvailableDevicesError: 'We could not find a camera or a microphone. Please make sure that you have allowed the browser to use your camera and microphone and that they are not being used by another application and try again.',
        initPublisherError: 'The operation failed due to an error. Please make sure you have allowed the browser to use your camera and microphone and that they are not being used by another application and try again.',
      },
      browserInfo: {
        browserSupportTitle: 'Browser support',
        supportedBrowsers: 'This test and interview support the latest versions of Chrome, Firefox, Safari and Edge.',
        mobileSupported: 'Supported browsers on mobile',
        android: 'Android: Google Chrome (latest release version), Firefox (latest release version)',
        iOS: 'iOS: Safari (latest release version)',
        latestVersion: 'Please make sure you are using the latest release version of the browser. This is to ensure that everything is working smoothly.',
        freezeInstruction: 'If the video recording doesn\'t start or the recording screen stays black please refresh the page.',
        unsupportedOnIos: 'It looks like you are using an unsupported browser on iOS. Please use Safari.',
        ieUnsupported: 'It looks like you are using Internet Explorer. Unfortunately Internet Explorer is not supported browser for this test or interview. Please use a different browser.',
        qualityTestNotSupported: 'Unfortunately testing the hardware is not supported on the browser you are using. You can continue to the interview without testing the audio and video quality, or alternatively use a different browser.',
      },
      reconnectSuccess: 'Reconnected successfully!',
      continue: 'Continue',
    },

    withdraw: {
      title: 'Not interested in this position anymore?',
      description: 'Withdraw yourself from the recruitment process.',
      buttonText: 'Withdraw from the recruitment process',
      thankYouTitle: 'Thank you!',
      thankYouText: 'You have now withdrawn yourself from the the recruitment process.',
      confirm: 'Are you sure you want to withdraw yourself from the recruitment process? After withdrawal you are no longer able to answer to this or any other interview related to this position.',
    },
  },

  page: {
    embeddedProfileInterview: {
      submit: 'Save & Submit',
      continue: 'Continue',
      supportCenter: 'Recright Help Center',
      thankYou:
        'Thank you for your video answer! We will be in touch with you in near future!',
    },

    supportLink: 'support@recright.com',

    finished: {
      reopenDescription: 'In case you closed this interview by accident, no worries! You can reopen the interview by clicking the button below!',
      reopenButtonText: 'Reopen interview',
      title: 'Thank you for taking the interview!',
      description1: 'Awesome! You have now answered all the interview questions, and your answers are on their way to the recruiter. Stay tuned!',
      descriptionsupport: mailto => `In case you closed this interview by accident, no worries! Just contact our Customer Support at ${mailto}, and we'll reopen the interview for you.`,
      descriptionfeedback: 'Your experience is very important to us! We would love to know how you felt about answering the video interview. How can we improve our services? You can share your feedback here:',
      feedbackbutton: 'Share your feedback',
      regularUrl: 'https://www.surveymonkey.com/r/B3ZCSWT',
      timedUrl: 'https://www.surveymonkey.com/r/MZMLPPT',
    },
    messages: {
      title: 'Personal message',
    },

    closed: {
      title: 'Interview is completed or interview has ended',
      description: 'The video interview you were invited to has already ended or you have completed your submission.',
      publicTitle: 'Interview has ended',
      publicDescription: 'The video interview you were looking for has already ended.',
    },
    saved: {
      title: 'Your video interview is now saved!',
      description: 'When you wish to continue with the video interview, use the link you have previously received to your e-mail. Already recorded video answers will be in place when you get back! Good luck for job hunt!',
      remember: 'Remember to send the answers to the recruiter after you have finished answering to the interview.',
      deleteHistory: 'If you are doing video interview in a public computer, please remember to clear your browsing history. In that way your interview is safe.',
    },
    unpublished: {
      title: 'The interview has not opened yet',
      publishTime: publishTime => `The interview will open ${publishTime}`,
    },
  },
}
